import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { AddPaymentsRequestViewModel } from '../models/AddPaymentsRequestViewModel';
import type { AdvancePaymentConfirmationRequestViewModel } from '../models/AdvancePaymentConfirmationRequestViewModel';
import type { BkmConfirmRequestViewModel } from '../models/BkmConfirmRequestViewModel';
import type { BkmStartShoppingRequestViewModel } from '../models/BkmStartShoppingRequestViewModel';
import type { BkmStartShoppingViewModel } from '../models/BkmStartShoppingViewModel';
import type { ChippinStartShoppingRequestViewModel } from '../models/ChippinStartShoppingRequestViewModel';
import type { ChippinTransactionStatusViewModel } from '../models/ChippinTransactionStatusViewModel';
import type { ConfirmPaymentModel } from '../models/ConfirmPaymentModel';
import type { ConfirmPaymentRequestViewModel } from '../models/ConfirmPaymentRequestViewModel';
import type { EmptyResponseModel } from '../models/EmptyResponseModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { GarantiPayConfirmPaymentRequestViewModel } from '../models/GarantiPayConfirmPaymentRequestViewModel';
import type { GarantiPayConfirmPaymentViewModel } from '../models/GarantiPayConfirmPaymentViewModel';
import type { GarantiPaySecurePaymentRequestViewModel } from '../models/GarantiPaySecurePaymentRequestViewModel';
import type { GarantiPaySecurePaymentViewModel } from '../models/GarantiPaySecurePaymentViewModel';
import type { GetParoOrGiftCardPointViewModel } from '../models/GetParoOrGiftCardPointViewModel';
import type { GetSeturCardViewModel } from '../models/GetSeturCardViewModel';
import type { GetSeturPuanViewModel } from '../models/GetSeturPuanViewModel';
import type { HopiCompleteCoinTransactionRequestViewModel } from '../models/HopiCompleteCoinTransactionRequestViewModel';
import type { HopiGetBirdUserInfoViewModel } from '../models/HopiGetBirdUserInfoViewModel';
import type { HopiStartCoinTransactionRequestViewModel } from '../models/HopiStartCoinTransactionRequestViewModel';
import type { HopiStartCoinTransactionViewModel } from '../models/HopiStartCoinTransactionViewModel';
import type { HotelGetAdvancePaymentRequestViewModel } from '../models/HotelGetAdvancePaymentRequestViewModel';
import type { HotelGetAdvancePaymentViewModel } from '../models/HotelGetAdvancePaymentViewModel';
import type { PaymentEventRequestViewModel } from '../models/PaymentEventRequestViewModel';
import type { PaymentInstallmentViewModel } from '../models/PaymentInstallmentViewModel';
import type { PaymentRequestForAdvancePaymentViewModel } from '../models/PaymentRequestForAdvancePaymentViewModel';
import type { PaymentRequestViewModel } from '../models/PaymentRequestViewModel';
import type { PaymentResultViewModel } from '../models/PaymentResultViewModel';
import type { PayWithParoOrGiftCardRequestViewModel } from '../models/PayWithParoOrGiftCardRequestViewModel';
import type { PayWithParoOrGiftCardViewModel } from '../models/PayWithParoOrGiftCardViewModel';
import type { PayWithSeturCardRequestViewModel } from '../models/PayWithSeturCardRequestViewModel';
import type { PayWithSeturPuanRequestViewModel } from '../models/PayWithSeturPuanRequestViewModel';
import type { PointQueryRequestViewModel } from '../models/PointQueryRequestViewModel';
import type { PointsViewModel } from '../models/PointsViewModel';
import type { PointUsingRequestViewModel } from '../models/PointUsingRequestViewModel';
import type { SecurePaymentRequestViewModel } from '../models/SecurePaymentRequestViewModel';
import type { SecurePaymentViewModel } from '../models/SecurePaymentViewModel';
import type { SeturCardSendApproveCodeRequestViewModel } from '../models/SeturCardSendApproveCodeRequestViewModel';
import type { SeturPuanRefundRequestViewModel } from '../models/SeturPuanRefundRequestViewModel';
import type { StartPaymentViewModel } from '../models/StartPaymentViewModel';
import type { WorldPayConfirmPaymentRequestViewModel } from '../models/WorldPayConfirmPaymentRequestViewModel';
import type { WorldPayConfirmPaymentViewModel } from '../models/WorldPayConfirmPaymentViewModel';
import type { WorldPayGenerateTokenRequestV2ViewModel } from '../models/WorldPayGenerateTokenRequestV2ViewModel';
import type { WorldPayGenerateTokenViewModel } from '../models/WorldPayGenerateTokenViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class PaymentsService {
  /**
   * Start
   * Start payment
   * @returns StartPaymentViewModel Success
   * @throws ApiError
   */
  public static async startPayment(
    {
      basketId,
    }: {
      basketId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<StartPaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<StartPaymentViewModel>({
      method: 'GET',
      url: `/v2/payments/start`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Finish
   * Finish payment
   * @returns PaymentResultViewModel Success
   * @throws ApiError
   */
  public static async finishPayment(
    {
      b,
      t,
    }: {
      /**
       * Basket id
       */
      b?: string;
      /**
       * Payment token
       */
      t?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<PaymentResultViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<PaymentResultViewModel>>({
      method: 'GET',
      url: `/v2/payments/finish`,
      query: {
        ...(b ? { b: b } : {}),
        ...(t ? { t: t } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Request
   * Makes a payment request
   * @returns PaymentRequestViewModel Success
   * @throws ApiError
   */
  public static async requestPayment(
    {
      paymentRequestId,
    }: {
      /**
       * Payment request id
       */
      paymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PaymentRequestViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<PaymentRequestViewModel>({
      method: 'GET',
      url: `/v2/payments/request`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Secure
   * Makes a secure payment
   * @returns SecurePaymentViewModel Success
   * @throws ApiError
   */
  public static async securePayment(
    {
      body,
    }: {
      body?: SecurePaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SecurePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<SecurePaymentViewModel>({
      method: 'POST',
      url: `/v2/payments/secure`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm
   * Confirms the payment
   * @returns ConfirmPaymentModel Success
   * @throws ApiError
   */
  public static async confirmPayment(
    {
      body,
    }: {
      body?: ConfirmPaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ConfirmPaymentModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<ConfirmPaymentModel>({
      method: 'POST',
      url: `/v2/payments/confirm`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Installments
   * Gets installment info
   * @returns PaymentInstallmentViewModel Success
   * @throws ApiError
   */
  public static async getInstallment(
    {
      paymentRequestId,
      amount,
      cardNo,
      installmentPostponement,
    }: {
      /**
       * Payment request id
       */
      paymentRequestId: string;
      /**
       * Amount
       */
      amount: number;
      /**
       * Card number
       */
      cardNo: string;
      installmentPostponement?: boolean;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PaymentInstallmentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<PaymentInstallmentViewModel>({
      method: 'GET',
      url: `/v2/payments/installments`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(amount ? { amount: amount } : {}),
        ...(cardNo ? { cardNo: cardNo } : {}),
        ...(installmentPostponement
          ? { installmentPostponement: installmentPostponement }
          : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (Chippin)
   * Starts shopping for chippin
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async chippinStartShopping(
    {
      body,
    }: {
      body?: ChippinStartShoppingRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/chippin/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Status (Chippin)
   * Gets chippin payment status
   * @returns ChippinTransactionStatusViewModel Success
   * @throws ApiError
   */
  public static async chippinPaymentStatus(
    {
      paymentRequestId,
    }: {
      paymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ChippinTransactionStatusViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<ChippinTransactionStatusViewModel>({
      method: 'GET',
      url: `/v2/payments/chippin/status`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Exit (Chippin)
   * Cashier exit for chippin
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async chippinCashierExit(
    {
      paymentRequestId,
    }: {
      paymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/payments/chippin/exit`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Return (Chippin)
   * Returns shopping for chippin
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async chippinShoppingReturn(
    {
      paymentRequestId,
      paymentId,
    }: {
      paymentRequestId?: string;
      paymentId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/payments/chippin/return`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paymentId ? { paymentId: paymentId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Points (Paro)
   * Gets paro or gift cards points
   * @returns GetParoOrGiftCardPointViewModel Success
   * @throws ApiError
   */
  public static async getParoOrGiftCardsPoint(
    {
      paymentRequestId,
      paroNumber,
    }: {
      paymentRequestId?: string;
      paroNumber?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetParoOrGiftCardPointViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetParoOrGiftCardPointViewModel>({
      method: 'GET',
      url: `/v2/payments/paro/points`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paroNumber ? { paroNumber: paroNumber } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (Paro)
   * Pays with paro or gift card
   * @returns PayWithParoOrGiftCardViewModel Success
   * @throws ApiError
   */
  public static async payWithParoOrGiftCard(
    {
      body,
    }: {
      body?: PayWithParoOrGiftCardRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PayWithParoOrGiftCardViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<PayWithParoOrGiftCardViewModel>({
      method: 'POST',
      url: `/v2/payments/paro/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Cancel (Paro)
   * Cancels paro or gift card payment
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async cancelParoOrGiftCardPayment(
    {
      paymentRequestId,
      paymentId,
    }: {
      paymentRequestId?: string;
      paymentId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/payments/paro/cancel`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paymentId ? { paymentId: paymentId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (GarantiPay)
   * Secure payment with garanti pay
   * @returns GarantiPaySecurePaymentViewModel Success
   * @throws ApiError
   */
  public static async garantiPaySecurePayment(
    {
      body,
    }: {
      body?: GarantiPaySecurePaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GarantiPaySecurePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GarantiPaySecurePaymentViewModel>({
      method: 'POST',
      url: `/v2/payments/garantipay/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm (GarantiPay)
   * Confirms payment for GarantiPay
   * @returns GarantiPayConfirmPaymentViewModel Success
   * @throws ApiError
   */
  public static async garantiPayConfirmPayment(
    {
      body,
    }: {
      body?: GarantiPayConfirmPaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GarantiPayConfirmPaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GarantiPayConfirmPaymentViewModel>({
      method: 'POST',
      url: `/v2/payments/garantipay/confirm`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Token (WorldPay)
   * Generates token for WorldPay
   * @returns WorldPayGenerateTokenViewModel Success
   * @throws ApiError
   */
  public static async worldPayGenerateToken(
    {
      body,
    }: {
      body?: WorldPayGenerateTokenRequestV2ViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WorldPayGenerateTokenViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<WorldPayGenerateTokenViewModel>({
      method: 'POST',
      url: `/v2/payments/worldpay/token`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm (WorldPay)
   * Confirms payment for WorldPay
   * @returns WorldPayConfirmPaymentViewModel Success
   * @throws ApiError
   */
  public static async worldPayConfirmPayment(
    {
      body,
    }: {
      body?: WorldPayConfirmPaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WorldPayConfirmPaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<WorldPayConfirmPaymentViewModel>({
      method: 'POST',
      url: `/v2/payments/worldpay/confirm`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (Hopi)
   * Get bird user info
   * @returns HopiGetBirdUserInfoViewModel Success
   * @throws ApiError
   */
  public static async hopiGetBirdUserInfo(
    {
      paymentRequestId,
      hopiToken,
    }: {
      paymentRequestId?: string;
      hopiToken?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HopiGetBirdUserInfoViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HopiGetBirdUserInfoViewModel>({
      method: 'GET',
      url: `/v2/payments/hopi/get`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(hopiToken ? { hopiToken: hopiToken } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (Hopi)
   * Start coin transaction for Hopi
   * @returns HopiStartCoinTransactionViewModel Success
   * @throws ApiError
   */
  public static async hopiStartCoinTransaction(
    {
      body,
    }: {
      body?: HopiStartCoinTransactionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HopiStartCoinTransactionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HopiStartCoinTransactionViewModel>({
      method: 'POST',
      url: `/v2/payments/hopi/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Complete (Hopi)
   * Complete coin transaction for Hopi
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async hopiCompleteCoinTransaction(
    {
      body,
    }: {
      body?: HopiCompleteCoinTransactionRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/hopi/complete`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Refund (Hopi)
   * Refunds hopi coins
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async hopiRefundCoins(
    {
      paymentRequestId,
      paymentId,
    }: {
      paymentRequestId?: string;
      paymentId?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/payments/hopi/refund`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(paymentId ? { paymentId: paymentId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (SeturCard)
   * Gets setur card balance
   * @returns GetSeturCardViewModel Success
   * @throws ApiError
   */
  public static async seturCardGet(
    {
      paymentRequestId,
      cardNo,
    }: {
      paymentRequestId?: string;
      cardNo?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetSeturCardViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetSeturCardViewModel>({
      method: 'GET',
      url: `/v2/payments/seturcard/get`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
        ...(cardNo ? { cardNo: cardNo } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Send (SeturCard)
   * Sends otp code request for setur card
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async seturCardSend(
    {
      body,
    }: {
      body?: SeturCardSendApproveCodeRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/seturcard/send`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Pay (SeturCard)
   * Pays with setur card
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async seturCardPay(
    {
      body,
    }: {
      body?: PayWithSeturCardRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/seturcard/pay`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Advance Payment (Hotel)
   * Gets advance payment for hotel
   * @returns HotelGetAdvancePaymentViewModel Success
   * @throws ApiError
   */
  public static async advancePaymentHotel(
    {
      body,
    }: {
      body?: HotelGetAdvancePaymentRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelGetAdvancePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<HotelGetAdvancePaymentViewModel>({
      method: 'POST',
      url: `/v2/payments/advance/hotel`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm (Advance Payment)
   * Confirms advance payment
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async advancePaymentConfirm(
    {
      body,
    }: {
      body?: AdvancePaymentConfirmationRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/advance/confirm`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (Points)
   * Gets points for credit card
   * @returns PointsViewModel Success
   * @throws ApiError
   */
  public static async getPoints(
    {
      body,
    }: {
      body?: PointQueryRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PointsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<PointsViewModel>({
      method: 'POST',
      url: `/v2/payments/points/get`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Pay (Points)
   * Pays points for credit card
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async payPoints(
    {
      body,
    }: {
      body?: PointUsingRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/points/pay`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Get (Setur Puan)
   * Gets setur puan balance for customer
   * @returns GetSeturPuanViewModel Success
   * @throws ApiError
   */
  public static async getSeturPuan(
    {
      paymentRequestId,
    }: {
      /**
       * Payment request id
       */
      paymentRequestId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<GetSeturPuanViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<GetSeturPuanViewModel>({
      method: 'GET',
      url: `/v2/payments/seturpuan/get`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Pay (Setur Puan)
   * Pays with setur puan balance
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async paySeturPuan(
    {
      body,
    }: {
      body?: PayWithSeturPuanRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/seturpuan/pay`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Insider
   * Gets the created date for insider from database by payment request identifier
   * @returns string Success
   * @throws ApiError
   */
  public static async getInsider(
    {
      paymentRequestId,
    }: {
      paymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'GET',
      url: `/v2/payments/insider`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Insider
   * Pushes the payment request identifier for insider to database
   * @returns string Success
   * @throws ApiError
   */
  public static async pushInsider(
    {
      paymentRequestId,
    }: {
      paymentRequestId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/v2/payments/insider`,
      query: {
        ...(paymentRequestId ? { paymentRequestId: paymentRequestId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Event
   * Gets the event for marketing tools from database by payment request identifier and type
   * @returns string Success
   * @throws ApiError
   */
  public static async getEvent(
    {
      pid,
      type,
    }: {
      /**
       * Payment request id
       */
      pid?: string;
      /**
       * Payment event types: gtm
       */
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'GET',
      url: `/v2/payments/events`,
      query: {
        ...(pid ? { pid: pid } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Event
   * Pushes the event for marketing tools
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async pushEvent(
    {
      body,
    }: {
      body?: PaymentEventRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/events`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Refund (Setur Puan)
   * Refunds setur puan balance
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async refundSeturPuan(
    {
      body,
    }: {
      body?: SeturPuanRefundRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/payments/seturpuan/refund`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Start (BKM)
   * Starts shopping for BKM
   * @returns BkmStartShoppingViewModel Success
   * @throws ApiError
   */
  public static async bkmStartPayment(
    {
      body,
    }: {
      body?: BkmStartShoppingRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BkmStartShoppingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<BkmStartShoppingViewModel>({
      method: 'POST',
      url: `/v2/payments/bkm/start`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Confirm (BKM)
   * Confirms payment for BKM
   * @returns EmptyResponseModel Success
   * @throws ApiError
   */
  public static async bkmConfirmPayment(
    {
      body,
    }: {
      body?: BkmConfirmRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseModel>({
      method: 'POST',
      url: `/v2/payments/bkm/confirm`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Cancel (BKM)
   * Cancels payment for BKM
   * @returns EmptyResponseModel Success
   * @throws ApiError
   */
  public static async bkmCancelPayment(
    {
      ticketId,
    }: {
      ticketId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseModel>({
      method: 'DELETE',
      url: `/v2/payments/bkm/cancel`,
      query: {
        ...(ticketId ? { ticketId: ticketId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Create (Advance)
   * Creates payment for advance payment
   * @returns PaymentRequestForAdvancePaymentViewModel Success
   * @throws ApiError
   */
  public static async advancePaymentCreate(
    {
      salesMasterId,
      authorization,
    }: {
      salesMasterId: number;
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PaymentRequestForAdvancePaymentViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<PaymentRequestForAdvancePaymentViewModel>({
      method: 'POST',
      url: `/v2/payments/advance/create/${salesMasterId}`,
      path: {
        ...(salesMasterId ? { salesMasterId: salesMasterId } : {}),
      },
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Add Payment
   * Adds payment record
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async add(
    {
      authorization,
      body,
    }: {
      authorization: string;
      body: Array<AddPaymentsRequestViewModel>;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/payments/add`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v2' {
  interface Services {
    PaymentsService: typeof PaymentsService;
  }
}
